import { useState } from "react";
import { useFormDataStore } from "../store/formDataStore";
import { debounce } from "../utils/debounce";
import apiClient from "../services/api/axiosConfig";

interface GeoNamesItem {
  name: string;
  countryName: string;
  adminName1?: string;
  lat: string;
  lng: string;
  countryCode?: string;
}

interface GeoNamesApiResponse {
  geonames: GeoNamesItem[];
}

interface TimezoneApiResponse {
  timezoneId: string;
}

interface UseCityAutoCompleteResult {
  citySuggestions: string[];
  showSuggestions: boolean;
  handleInputChange: (value: string) => void;
  handleCitySelect: (label: string) => Promise<void>;
}

export const useCityAutoComplete = (): UseCityAutoCompleteResult => {
  const { setFormData } = useFormDataStore();
  const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const fetchCities = async (query: string): Promise<void> => {
    if (!query || query.length < 2) return;
    try {
      const res = await apiClient.get<GeoNamesApiResponse>("/api/geo/search", {
        params: { name_startsWith: query },
      });
      const cities = res.data.geonames?.map(
        (item) =>
          `${item.name}, ${item.countryName}${item.adminName1 ? ` (${item.adminName1})` : ""}`
      );
      setCitySuggestions(cities || []);
    } catch (err) {
      console.error("Ошибка загрузки городов:", err);
    }
  };

  const debouncedFetchCities = debounce((value: unknown) => {
    if (typeof value === "string") {
      void fetchCities(value);
    }
  }, 300);

  const handleInputChange = (value: string): void => {
    setFormData({ placeOfBirth: value });
    setShowSuggestions(true);
    if (value.length >= 2) {
      debouncedFetchCities(value);
    } else {
      setCitySuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleCitySelect = async (label: string): Promise<void> => {
    setFormData({ placeOfBirth: label });
    setShowSuggestions(false);

    const cityName = label.split(",")[0];
    try {
      const res = await apiClient.get<GeoNamesApiResponse>("/api/geo/search", {
        params: { name_startsWith: cityName }, // ✅ исправлено
      });
      const cityData = res.data.geonames?.[0];
      if (!cityData) return;

      const timezoneRes = await apiClient.get<TimezoneApiResponse>("/api/geo/timezone", {
        params: { lat: cityData.lat, lng: cityData.lng },
      });

      setFormData({
        placeOfBirth: label,
        cityNameForApi: cityData.name,
        latitude: parseFloat(cityData.lat),
        longitude: parseFloat(cityData.lng),
        countryCode: cityData.countryCode || "RU",
        timezone: timezoneRes.data?.timezoneId || "Europe/Moscow",
      });
    } catch (error) {
      console.error("Ошибка при выборе города:", error);
    }
  };

  return {
    citySuggestions,
    showSuggestions,
    handleInputChange,
    handleCitySelect,
  };
};
